import { getAutogeneratedBanner } from '../banners';
import {
  assertNoFieldNameCollisions,
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  escapeBlockCommentEnd,
  getEnumMemberKind,
  getFieldDocLines,
  getSensitiveKeys,
  IDENTIFIER_SAFE_KEY,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

/** SCREAMING_SNAKE / snake → PascalCase (`DB_HOST` → `DbHost`). */
export function snakeToPascalCase(key: string): string {
  return key
    .split('_')
    .filter((p) => p.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

const CSHARP_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

// C# keywords + contextual that would fail as a property/class/namespace segment
const CSHARP_KEYWORDS = new Set([
  'abstract',
  'as',
  'base',
  'bool',
  'break',
  'byte',
  'case',
  'catch',
  'char',
  'checked',
  'class',
  'const',
  'continue',
  'decimal',
  'default',
  'delegate',
  'do',
  'double',
  'else',
  'enum',
  'event',
  'explicit',
  'extern',
  'false',
  'finally',
  'fixed',
  'float',
  'for',
  'foreach',
  'goto',
  'if',
  'implicit',
  'in',
  'int',
  'interface',
  'internal',
  'is',
  'lock',
  'long',
  'namespace',
  'new',
  'null',
  'object',
  'operator',
  'out',
  'override',
  'params',
  'private',
  'protected',
  'public',
  'readonly',
  'ref',
  'return',
  'sbyte',
  'sealed',
  'short',
  'sizeof',
  'stackalloc',
  'static',
  'string',
  'struct',
  'switch',
  'this',
  'throw',
  'true',
  'try',
  'typeof',
  'uint',
  'ulong',
  'unchecked',
  'unsafe',
  'ushort',
  'using',
  'virtual',
  'void',
  'volatile',
  'while',
  // contextual / reserved type names commonly rejected as type ids
  'record',
  'var',
  'dynamic',
]);

function isCsharpReserved(name: string): boolean {
  return CSHARP_KEYWORDS.has(name);
}

/** one or more identifiers joined by dots, e.g. `App.Config` */
const CSHARP_NAMESPACE = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;

type CsharpTypeKind = 'long' | 'double' | 'bool' | 'string' | 'object' | 'json';

function csharpTypeKind(coerced: CoercedType): CsharpTypeKind {
  if (coerced === 'int') return 'long';
  if (coerced === 'number') return 'double';
  if (coerced === 'boolean') return 'bool';
  if (coerced === 'object') return 'json';
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    const kind = getEnumMemberKind(coerced.enum);
    if (kind === 'int') return 'long';
    if (kind === 'number') return 'double';
    if (kind === 'boolean') return 'bool';
    if (kind === 'mixed') return 'object';
  }
  // typed arrays/records surface as JsonElement - consumers deserialize to the shape they want
  if (typeof coerced === 'object' && ('arrayOf' in coerced || 'recordOf' in coerced)) {
    return 'json';
  }
  return 'string';
}

function csharpFieldType(kind: CsharpTypeKind, isRequired: boolean): string {
  if (kind === 'json') return 'JsonElement';
  if (kind === 'object') return isRequired ? 'object' : 'object?';
  if (kind === 'string') return isRequired ? 'string' : 'string?';
  if (kind === 'long') return isRequired ? 'long' : 'long?';
  if (kind === 'double') return isRequired ? 'double' : 'double?';
  return isRequired ? 'bool' : 'bool?';
}

function csharpStringLiteral(text: string): string {
  return JSON.stringify(text);
}

function fieldDocComment(field: ResolvedFieldType): Array<string> {
  const docLines = getFieldDocLines(field);
  if (!docLines.length) return [];
  return [
    '    /// <summary>',
    ...docLines.map((line) => `    /// ${escapeBlockCommentEnd(line).replace(/--/g, '- -')}`),
    '    /// </summary>',
  ];
}

/** Statements that bind a local `{propName}` from `config`, then feed the object initializer. */
function csharpLoadLocal(field: ResolvedFieldType): Array<string> {
  const prop = snakeToPascalCase(field.key);
  const kind = csharpTypeKind(field.coerced);
  const type = csharpFieldType(kind, field.isRequired);
  const keyLit = csharpStringLiteral(field.key);
  const missing = `throw new InvalidOperationException("varlock: missing required key ${field.key} in __VARLOCK_ENV");`;

  const lines = [
    `        ${type} ${prop};`,
    `        if (config.TryGetProperty(${keyLit}, out var __entry_${prop}) && __entry_${prop}.TryGetProperty("value", out var __val_${prop}))`,
    '        {',
  ];

  if (kind === 'json') {
    lines.push(`            ${prop} = __val_${prop}.Clone();`);
  } else if (kind === 'object') {
    lines.push(`            ${prop} = JsonSerializer.Deserialize<object>(__val_${prop}.GetRawText())${field.isRequired ? '!' : ''};`);
  } else if (kind === 'string') {
    lines.push(`            ${prop} = __val_${prop}.GetString()${field.isRequired ? '!' : ''};`);
  } else if (kind === 'long') {
    lines.push(`            ${prop} = __val_${prop}.GetInt64();`);
  } else if (kind === 'double') {
    lines.push(`            ${prop} = __val_${prop}.GetDouble();`);
  } else {
    lines.push(`            ${prop} = __val_${prop}.GetBoolean();`);
  }

  lines.push('        }');
  lines.push('        else');
  lines.push('        {');
  if (field.isRequired) {
    lines.push(`            ${missing}`);
  } else if (kind === 'json') {
    lines.push(`            ${prop} = default;`);
  } else {
    lines.push(`            ${prop} = null;`);
  }
  lines.push('        }');
  return lines;
}

export function generateCsharpEnvSrc(
  fields: Array<ResolvedFieldType>,
  opts?: { namespace?: unknown, className?: unknown },
): string {
  const { safe, skipped } = partitionRepresentableKeys(
    fields,
    (k) => {
      const name = snakeToPascalCase(k);
      return !CSHARP_IDENTIFIER.test(name) || isCsharpReserved(name);
    },
  );
  assertNoFieldNameCollisions(safe, snakeToPascalCase, 'C#');

  const className = opts?.className === undefined ? 'Env' : String(opts.className);
  if (!IDENTIFIER_SAFE_KEY.test(className) || isCsharpReserved(className)) {
    throw new Error(`@generateCsharpEnv - \`class\` must be a valid C# class name (got ${JSON.stringify(opts?.className)})`);
  }

  let namespace: string | undefined;
  if (opts?.namespace !== undefined && String(opts.namespace).trim() !== '') {
    namespace = String(opts.namespace).trim();
    if (!CSHARP_NAMESPACE.test(namespace) || namespace.split('.').some(isCsharpReserved)) {
      throw new Error(`@generateCsharpEnv - \`namespace\` must be a valid C# namespace (got ${JSON.stringify(opts?.namespace)})`);
    }
  }

  const sensitiveKeys = getSensitiveKeys(fields);
  const sensitiveInit = sensitiveKeys.length
    ? `new HashSet<string> { ${sensitiveKeys.map(csharpStringLiteral).join(', ')} }`
    : 'new HashSet<string>()';

  const src = [
    getAutogeneratedBanner('//'),
    ...(namespace ? ['', `namespace ${namespace};`] : []),
    '',
    'using System;',
    'using System.Collections.Generic;',
    'using System.Text.Json;',
    '',
    ...skippedKeysComment(skipped, '//'),
    `public sealed class ${className}`,
    '{',
    '    /// <summary>Keys that hold sensitive values — use to build your own redaction / leak-scanning.</summary>',
    `    public static readonly IReadOnlySet<string> SensitiveKeys = ${sensitiveInit};`,
    '',
    ...safe.flatMap((field) => {
      const name = snakeToPascalCase(field.key);
      const type = csharpFieldType(csharpTypeKind(field.coerced), field.isRequired);
      // required reference/value props use `required` so the object initializer is complete
      const requiredMod = field.isRequired ? 'required ' : '';
      return [
        ...fieldDocComment(field),
        `    public ${requiredMod}${type} ${name} { get; init; }`,
      ];
    }),
    '',
    '    /// <summary>Parse the __VARLOCK_ENV blob (injected by <c>varlock run</c>) into a typed Env.</summary>',
    `    public static ${className} Load()`,
    '    {',
    '        var blob = Environment.GetEnvironmentVariable("__VARLOCK_ENV");',
    '        if (string.IsNullOrEmpty(blob))',
    `            throw new InvalidOperationException(${csharpStringLiteral(BLOB_MISSING_MSG)});`,
    `        if (blob.StartsWith(${csharpStringLiteral(ENCRYPTED_BLOB_PREFIX)}, StringComparison.Ordinal))`,
    `            throw new InvalidOperationException(${csharpStringLiteral(BLOB_ENCRYPTED_MSG)});`,
    '',
    '        using var doc = JsonDocument.Parse(blob);',
    '        if (!doc.RootElement.TryGetProperty("config", out var config) || config.ValueKind != JsonValueKind.Object)',
    '            throw new InvalidOperationException("varlock: __VARLOCK_ENV missing config object");',
    '',
    ...safe.flatMap(csharpLoadLocal),
    '',
    `        return new ${className}`,
    '        {',
    // explicit `Prop = Prop` — a bare `{ Prop, }` is a collection initializer, not property shorthand
    ...safe.map((field) => {
      const name = snakeToPascalCase(field.key);
      return `            ${name} = ${name},`;
    }),
    '        };',
    '    }',
    '}',
    '',
  ];

  return src.join('\n');
}
